import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HTMLAttributes } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { useWizardMessages } from "../../components/profile/onboarding/hooks/useWizardMessages";
import { WIZARD_DELAYS } from "../../components/profile/onboarding/constants";
import {
  onlyDigits,
  formatPhoneBr,
  formatCpf,
  formatCep,
} from "../../helpers/cadastroUtils";
import {
  validateAddressComplete,
  validateCpfField,
  validatePhoneBrDigits,
} from "../../helpers/paymentProfileValidation";
import {
  applyAuthToken,
  getRegisterErrorMessage,
  registerUser,
} from "../../services/register";
import { fetchViaCep } from "../../services/viacep";
import {
  type PaymentProfileAddress,
} from "../../services/pagbank";
import {
  BOT_QUESTIONS,
  INPUT_LABELS,
  MIN_SIGNUP_AGE,
  STEP_TITLES,
  type CadastroStep,
} from "./constants";

export function useCadastroWizard() {
  const navigate = useNavigate();
  const { refreshToken } = useAuth();
  const registrationTokenRef = useRef<string | null>(null);
  const { messages, isBotTyping, addBotMessage, addUserMessage } =
    useWizardMessages({ userId: undefined });

  const [step, setStep] = useState<CadastroStep>("nome");
  const [inputValue, setInputValue] = useState("");
  const [processing, setProcessing] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");

  const [addr, setAddr] = useState<PaymentProfileAddress>({
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    postal_code: "",
  });

  const started = useRef(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isBotTyping, scrollToBottom]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    addBotMessage(
      "Oi, que bom ter você aqui! 👋\n\nEu sou o assistente do Easyfit e vou te guiar passo a passo para criar sua conta agora. É rapidinho e super seguro 🔒\n\nVamos começar o seu cadastro?",
      { delay: WIZARD_DELAYS.SHORT, wizardType: "info" },
    );
    addBotMessage(BOT_QUESTIONS.nome, { delay: WIZARD_DELAYS.MEDIUM });
  }, [addBotMessage]);

  const next = useCallback(
    (s: Exclude<CadastroStep, "success">) => {
      addBotMessage(BOT_QUESTIONS[s], {});
      setStep(s);
      setInputValue("");
    },
    [addBotMessage],
  );

  const handleRegister = useCallback(async (): Promise<boolean> => {
    try {
      const data = await registerUser({
        name,
        email,
        password,
        birth_date: birthDate,
        phone,
      });
      registrationTokenRef.current = data.token;
      applyAuthToken(data.token);
      return true;
    } catch (e: unknown) {
      const msg = getRegisterErrorMessage(e);
      addBotMessage(msg, { wizardType: "error" });
      toast.error(msg);
      return false;
    }
  }, [addBotMessage, birthDate, email, name, password, phone]);

  const finishSignupSuccess = useCallback(() => {
    setStep("success");
    addBotMessage(
      "Tudo certo! Sua conta foi cadastrada com sucesso. Bem-vindo ao Easyfit!",
      { wizardType: "confirmation", delay: 400 },
    );
    const tok = registrationTokenRef.current;
    if (tok) refreshToken(tok);
    setTimeout(() => navigate("/appointments", { replace: true }), 1600);
  }, [addBotMessage, navigate, refreshToken]);

  const submitStep = useCallback(async () => {
    const v = inputValue.trim();
    if (!v) return;

    if (step === "nome") {
      const parts = v.split(" ").filter(Boolean);
      if (parts.length < 2 || parts[0].length < 2 || parts[1].length < 2) {
        toast.error("Informe nome e sobrenome.");
        return;
      }
      setName(v);
      addUserMessage(v);
      next("email");
      return;
    }

    if (step === "email") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        toast.error("E-mail inválido.");
        return;
      }
      setEmail(v);
      addUserMessage(v);
      next("senha");
      return;
    }

    if (step === "senha") {
      if (v.length < 8) {
        toast.error("A senha deve ter pelo menos 8 caracteres.");
        return;
      }
      if (!/[0-9]/.test(v)) {
        toast.error(
          "A senha deve ter pelo menos 8 caracteres e incluir ao menos um número.",
        );
        return;
      }
      setPassword(v);
      addUserMessage("••••••••");
      next("nascimento");
      return;
    }

    if (step === "nascimento") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        toast.error("Use o formato YYYY-MM-DD.");
        return;
      }
      const date = new Date(v);
      if (Number.isNaN(date.getTime())) {
        toast.error("Data de nascimento inválida.");
        return;
      }
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const mDiff = today.getMonth() - date.getMonth();
      if (mDiff < 0 || (mDiff === 0 && today.getDate() < date.getDate())) {
        age -= 1;
      }
      if (age < MIN_SIGNUP_AGE) {
        toast.error(
          `Você precisa ter pelo menos ${MIN_SIGNUP_AGE} anos para se cadastrar.`,
        );
        return;
      }
      setBirthDate(v);
      addUserMessage(v.split("-").reverse().join("/"));
      next("telefone");
      return;
    }

    if (step === "telefone") {
      const phoneErr = validatePhoneBrDigits(v);
      if (phoneErr) {
        toast.error(phoneErr);
        return;
      }
      setPhone(formatPhoneBr(v));
      addUserMessage(formatPhoneBr(v));
      setProcessing(true);
      const ok = await handleRegister();
      setProcessing(false);
      if (!ok) return;
      next("cpf");
      return;
    }

    if (step === "cpf") {
      const cpfErr = validateCpfField(v);
      if (cpfErr) {
        toast.error(cpfErr);
        return;
      }
      setCpf(formatCpf(v));
      addUserMessage(formatCpf(v));
      next("cep");
      return;
    }

    if (step === "cep") {
      const cepDigits = onlyDigits(v);
      if (cepDigits.length !== 8) {
        toast.error("CEP inválido.");
        return;
      }
      addUserMessage(formatCep(cepDigits));
      setProcessing(true);
      try {
        const viaCep = await fetchViaCep(cepDigits);
        const parsedAddr: PaymentProfileAddress = {
          postal_code: cepDigits,
          street: viaCep.logradouro || "",
          district: viaCep.bairro || "",
          city: viaCep.localidade || "",
          state: viaCep.uf || "",
          number: "",
          complement: "",
        };
        if (
          !parsedAddr.street ||
          !parsedAddr.district ||
          !parsedAddr.city ||
          !parsedAddr.state
        ) {
          throw new Error("CEP incompleto");
        }
        setAddr(parsedAddr);
        addBotMessage(
          `Encontrei: ${parsedAddr.street}, ${parsedAddr.district} - ${parsedAddr.city}/${parsedAddr.state}.`,
          { wizardType: "info" },
        );
        next("numero");
      } catch {
        addBotMessage(
          "Não consegui localizar esse CEP. Confira e tente novamente.",
          { wizardType: "error" },
        );
      } finally {
        setProcessing(false);
      }
      return;
    }

    if (step === "numero") {
      if (!v) {
        toast.error("Informe o número.");
        return;
      }
      setAddr((prev) => ({ ...prev, number: v }));
      addUserMessage(v);
      next("complemento");
      return;
    }

    if (step === "complemento") {
      const complemento =
        v.toLowerCase().includes("sem complemento") ||
        v.toLowerCase() === "sem"
          ? ""
          : v;
      const nextAddr = { ...addr, complement: complemento };
      const addrErr = validateAddressComplete(nextAddr);
      if (addrErr) {
        addBotMessage(addrErr, { wizardType: "error" });
        toast.error(addrErr);
        return;
      }
      setAddr(nextAddr);
      addUserMessage(complemento || "Sem complemento");
      finishSignupSuccess();
    }
  }, [
    addBotMessage,
    addUserMessage,
    addr,
    finishSignupSuccess,
    handleRegister,
    inputValue,
    next,
    step,
  ]);

  const handleInputChange = useCallback(
    (val: string) => {
      if (step === "telefone") {
        setInputValue(formatPhoneBr(val));
        return;
      }
      if (step === "cpf") {
        setInputValue(formatCpf(val));
        return;
      }
      if (step === "cep") {
        setInputValue(formatCep(val));
        return;
      }
      setInputValue(val);
    },
    [step],
  );

  const highLevelIndex = useMemo(() => {
    if (step === "nome") return 1;
    if (step === "email") return 2;
    if (step === "senha") return 3;
    if (step === "nascimento") return 4;
    if (step === "telefone") return 5;
    if (step === "cpf") return 6;
    if (["cep", "numero", "complemento"].includes(step)) return 7;
    return 7;
  }, [step]);

  const desktopProgress = Math.max(
    8,
    Math.round((highLevelIndex / STEP_TITLES.length) * 100),
  );

  const inputMeta = useMemo(() => {
    if (step === "success") {
      return {
        label: "",
        placeholder: "",
        inputType: "text" as const,
        autoComplete: "off" as const,
        inputMode: undefined as HTMLAttributes<HTMLInputElement>["inputMode"],
      };
    }
    const s = step as Exclude<CadastroStep, "success">;
    const placeholder =
      step === "telefone"
        ? "(11) 99999-9999"
        : step === "cpf"
          ? "000.000.000-00"
          : step === "cep"
            ? "00000-000"
            : step === "nascimento"
          ? "Selecione sua data"
          : step === "complemento"
            ? "Sem complemento"
            : "Digite e envie";

    const inputType =
      step === "senha"
        ? ("password" as const)
        : step === "nascimento"
          ? ("date" as const)
          : step === "email"
            ? ("email" as const)
            : step === "telefone"
              ? ("tel" as const)
              : ("text" as const);

    const autoComplete =
      step === "email"
        ? ("email" as const)
        : step === "senha"
          ? ("new-password" as const)
          : step === "telefone"
            ? ("tel" as const)
            : step === "nascimento"
              ? ("bday" as const)
              : ("off" as const);

    const inputMode:
      | HTMLAttributes<HTMLInputElement>["inputMode"]
      | undefined = ["telefone", "cep"].includes(step)
      ? "numeric"
      : undefined;

    return {
      label: INPUT_LABELS[s],
      placeholder,
      inputType,
      autoComplete,
      inputMode,
    };
  }, [step]);

  const showTextInput = step !== "success" && !isBotTyping;

  return {
    messages,
    isBotTyping,
    messagesContainerRef,
    messagesEndRef,
    step,
    inputValue,
    processing,
    submitStep,
    handleInputChange,
    inputMeta,
    showTextInput,
    highLevelIndex,
    desktopProgress,
    stepTitles: STEP_TITLES,
  };
}

export type CadastroWizardReturn = ReturnType<typeof useCadastroWizard>;
